import { useEffect, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { DashboardShell } from "@/components/Shell";
import { BarChart3, TrendingUp, Trophy } from "lucide-react";

export default function AdminStats() {
  const [stats, setStats] = useState(null);
  const [leaderStaff, setLeaderStaff] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/leaderboards/staff", { params: { limit: 20 } }).then((r) => setLeaderStaff(r.data));
  }, []);

  return (
    <DashboardShell role="admin">
      <div className="mb-8">
        <div className="text-xs tracking-[0.25em] uppercase text-[#EBB300]">Statistiken</div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mt-2 flex items-center gap-3"><BarChart3 className="w-9 h-9 text-[#EBB300]" /> Insights</h1>
      </div>

      {/* Top staff */}
      <div className="bg-[#121216] border border-white/5 rounded-2xl p-6 mb-6">
        <h2 className="text-xs tracking-[0.25em] uppercase text-[#EBB300] mb-4 flex items-center gap-2"><Trophy className="w-3.5 h-3.5" /> Mitarbeiter-Ranking</h2>
        <div className="space-y-2" data-testid="admin-stats-top-staff">
          {leaderStaff.map((s, i) => (
            <div key={s.staff_id} className="flex items-center gap-4 p-3 bg-[#0A0A0C] rounded-xl">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-black ${i === 0 ? "bg-[#EBB300] text-[#0A0A0C]" : i === 1 ? "bg-white/10 text-white" : i === 2 ? "bg-[#E05A33]/30 text-[#E05A33]" : "bg-white/5 text-[#A1A1AA]"}`}>
                {i + 1}
              </div>
              {s.photo_url && <img src={s.photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{s.name}</div>
                <div className="text-xs text-[#A1A1AA]">{s.count} Trinkgelder</div>
              </div>
              <div className="font-black text-[#EBB300]">{formatEUR(s.total)}</div>
            </div>
          ))}
          {leaderStaff.length === 0 && <div className="text-sm text-[#A1A1AA]">Noch keine Daten.</div>}
        </div>
      </div>

      {/* Per location */}
      <div className="bg-[#121216] border border-white/5 rounded-2xl p-6 mb-6">
        <h2 className="text-xs tracking-[0.25em] uppercase text-[#EBB300] mb-4 flex items-center gap-2"><TrendingUp className="w-3.5 h-3.5" /> Trinkgelder pro Location</h2>
        <div className="space-y-3" data-testid="admin-stats-per-location">
          {(stats?.per_location || []).length === 0 && <div className="text-sm text-[#A1A1AA]">Keine Daten.</div>}
          {(stats?.per_location || []).map((l) => {
            const max = Math.max(...(stats?.per_location || []).map((x) => x.total), 1);
            const pct = (l.total / max) * 100;
            return (
              <div key={l.location_id}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-semibold">{l.name}</span>
                  <span className="text-[#EBB300] font-bold">{formatEUR(l.total)} <span className="text-[#A1A1AA] font-normal">· {l.count}×</span></span>
                </div>
                <div className="h-3 bg-[#0A0A0C] rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#EBB300] to-[#FAD02C]" style={{ width: `${pct}%` }}></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Timeseries */}
      <div className="bg-[#121216] border border-white/5 rounded-2xl p-6">
        <h2 className="text-xs tracking-[0.25em] uppercase text-[#EBB300] mb-4">Verlauf (30 Tage)</h2>
        <div className="flex items-end gap-1 h-48" data-testid="admin-stats-timeseries">
          {(stats?.timeseries || []).map((d, i) => {
            const max = Math.max(...(stats?.timeseries || []).map((x) => x.amount), 1);
            const h = Math.max((d.amount / max) * 100, 2);
            return (
              <div key={i} className="flex-1 group relative">
                <div className="w-full bg-[#EBB300]/30 hover:bg-[#EBB300] transition-colors rounded-t" style={{ height: `${h}%` }}></div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-[#0A0A0C] border border-white/10 rounded px-2 py-1 text-[10px] whitespace-nowrap">
                  {d.date} · {formatEUR(d.amount)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardShell>
  );
}
